/*!
 * Modifications (c) James Grayson - Grayson Studio Ltd.
 * Ref: Invoice 0022
 * Unlicensed IP - Property of Grayson Studio. Unauthorized use prohibited under Copyright, Designs and Patents Act 1988.
 * Signature: 8d22f327
 */
const _COPYRIGHT_AUTH_HASH = "8d22f327";

/* Selling plan names that get a hidden `_plan_name` line-item property on their refill(s).
   Kept as an explicit allowlist so one-time purchases (and any future plans) don't pick it up
   unless deliberately added here. Stored lowercase; matching is case-insensitive + trimmed,
   so "Discovery Plan", "Discovery plan", " discovery plan " all match. */
const PLAN_NAME_PROPERTY_ALLOWLIST = ["starter plan", "discovery plan", "first refill free"];

class BundleBuilder extends HTMLElement {
  static revision = "63a91e95";

  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.refillsCollectionId = this.dataset.refillsCollectionId;
    // Optional dedicated Recharge bundle product for the Discovery Plan. When unset, the
    // bundle parent falls back to this page's bundle product + the selected plan variant.
    this.bundleProductId = this.dataset.bundleProductId || null;
    this.bundleVariantId = this.dataset.bundleVariantId || null;
    this.bundleSellingPlanId = this.dataset.bundleSellingPlanId || null;
    this.state = {
      variant: null,
      shell: null,
      refills: [],
    };
  }

  connectedCallback() {
    this.cacheElements();
    this.readLabels();
    this.readIngredients();
    this.bindPlanEvents();
    this.bindShellEvents();
    this.bindScentEvents();
    this.bindModalEvents();
    this.bindCtaEvents();
    this.syncPlanFromDom();
    this.updateScentTitle();
    this.renderShellHeaderPrice();
    this.renderScentHeaderPrice();
    this.renderCta();
  }

  /* ----- DOM cache ----- */
  cacheElements() {
    this.heroImage = this.querySelector(".bundle-builder__hero");

    this.planInputs = this.querySelectorAll(".bundle-plan__input");
    this.shellInputs = this.querySelectorAll(".bundle-shell__input");
    this.scentInputs = this.querySelectorAll(".bundle-scent__input");

    this.shellStep = this.querySelector(".bundle-step--shell");
    this.scentStep = this.querySelector(".bundle-step--scent");
    this.planStep = this.querySelector(".bundle-step--plan");
    this.shellChoiceEl = this.querySelector(".bundle-step--shell [data-step-choice]");
    this.shellChoiceTextEl = this.querySelector(".bundle-step--shell [data-step-choice-text]");
    this.scentChoiceEl = this.querySelector(".bundle-step--scent [data-step-choice]");
    this.scentChoiceTextEl = this.querySelector(".bundle-step--scent [data-step-choice-text]");
    this.planChoiceEl = this.querySelector(".bundle-step--plan [data-step-choice]");
    this.planChoiceTextEl = this.querySelector(".bundle-step--plan [data-step-choice-text]");

    this.ctaBtn = this.querySelector("[data-add-to-cart]");
    this.ctaPrice = this.querySelector("[data-cta-price]");
    this.ctaText = this.querySelector(".bundle-cta--get-set .bundle-cta__text");

    this.scentTitle = this.querySelector("[data-scent-pick-label]");

    this.shellHeaderPriceEl = this.querySelector('[data-step-header-price="shell"]');
    this.scentHeaderPriceEl = this.querySelector('[data-step-header-price="scent"]');

    // All three accordion bodies (ingredients, howtouse, howrefills).
    this.accordionBodies = {};
    this.accordionFallbacks = {};
    ["ingredients", "howtouse", "howrefills"].forEach((key) => {
      const el = this.querySelector(`[data-accordion-body="${key}"]`);
      if (el) {
        this.accordionBodies[key] = el;
        this.accordionFallbacks[key] = el.innerHTML;
      }
    });

    this.subscriptionModal = this.querySelector("[data-subscription-modal]");
    this.modalOpenBtns = this.querySelectorAll("[data-open-subscription-modal]");
    this.modalCloseBtn = this.querySelector("[data-close-subscription-modal]");
  }

  readLabels() {
    const el = this.querySelector("[data-bundle-labels]");
    this.labels = el ? JSON.parse(el.textContent) : { pick_1: "Pick a scent" };
  }

  readIngredients() {
    const el = this.querySelector("[data-accordion-data]");
    try {
      this.accordionData = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      console.warn("bundle-builder: failed to parse accordion data", e);
      this.accordionData = {};
    }
  }

  /* ----- Events ----- */

  bindPlanEvents() {
    this.planInputs.forEach((input) =>
      input.addEventListener("change", () => this.onPlanChange(input))
    );
  }

  bindShellEvents() {
    this.shellInputs.forEach((input) =>
      input.addEventListener("change", () => this.onShellChange(input))
    );
  }

  bindScentEvents() {
    this.scentInputs.forEach((input) =>
      input.addEventListener("change", () => this.onScentToggle(input))
    );
  }

  bindModalEvents() {
    this.modalOpenBtns.forEach((btn) =>
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.subscriptionModal?.showModal) this.subscriptionModal.showModal();
      })
    );
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener("click", () => this.subscriptionModal?.close?.());
    }
    if (this.subscriptionModal) {
      this.subscriptionModal.addEventListener("click", (e) => {
        if (e.target === this.subscriptionModal) this.subscriptionModal.close();
      });
    }
  }

  bindCtaEvents() {
    if (this.ctaBtn) {
      this.ctaBtn.addEventListener("click", () => this.submitBundle());
    }
  }

  /* ----- State updates ----- */

  syncPlanFromDom() {
    const checked = [...this.planInputs].find((i) => i.checked);
    if (checked) this.onPlanChange(checked);
  }

  onPlanChange(input) {
    const card = input.closest(".bundle-plan");
    const sellingPlanName = card.dataset.sellingPlanName?.trim() || null;
    this.state.variant = {
      id: card.dataset.variantId,
      refillCount: parseInt(card.dataset.refillCount, 10),
      sellingPlanName: sellingPlanName,
      sellingPlanId: card.dataset.sellingPlanId || null,
      isFirstRefillFree: card.dataset.isFirstRefillFree === "true",
      displayPrice: parseInt(card.dataset.displayPrice, 10),
      comparePrice: parseInt(card.dataset.comparePrice, 10) || 0,
      savePercent: parseInt(card.dataset.savePercent, 10) || 0,
      card: card,
    };
    const planLabel = card.querySelector(".bundle-plan__title")?.textContent?.trim();
    this.setHeadingChoice(this.planChoiceEl, this.planChoiceTextEl, planLabel);
    this.updateScentTitle();
    this.syncSubscriptionModalBody();
    this.renderScentHeaderPrice();
    this.renderCta();
  }

  syncSubscriptionModalBody() {
    const bodyEl = this.querySelector("[data-subscription-modal-body]");
    if (!bodyEl || !this.state.variant?.card) return;
    const tpl = this.state.variant.card.querySelector("[data-plan-modal-body]");
    if (tpl && tpl.innerHTML.trim()) {
      bodyEl.innerHTML = tpl.innerHTML;
    }
  }

  onShellChange(input) {
    const card = input.closest(".bundle-shell");
    const shellLabel = card.querySelector(".bundle-shell__label")?.textContent?.trim();
    this.state.shell = {
      productId: card.dataset.shellProductId,
      variantId: card.dataset.shellVariantId,
      price: parseInt(card.dataset.shellPrice, 10),
      handle: card.dataset.shellHandle,
      image: card.dataset.shellImage,
      title: shellLabel || card.dataset.shellHandle,
    };
    if (this.heroImage && this.state.shell.image) {
      this.heroImage.src = this.state.shell.image;
    }
    this.updateShellHeading(shellLabel);
    this.renderShellHeaderPrice();
    if (this.shellStep) this.shellStep.open = false;
    if (this.scentStep) this.scentStep.open = true;
    this.renderCta();
  }

  /* Shows/hides a step's tick + choice line below its heading, e.g. a tick icon
     followed by "Green" under "CHOOSE YOUR SHELL". */
  setHeadingChoice(choiceEl, choiceTextEl, text) {
    if (!choiceEl) return;
    if (text) {
      if (choiceTextEl) choiceTextEl.textContent = text;
      choiceEl.hidden = false;
    } else {
      if (choiceTextEl) choiceTextEl.textContent = "";
      choiceEl.hidden = true;
    }
  }

  updateShellHeading(shellLabel) {
    this.setHeadingChoice(this.shellChoiceEl, this.shellChoiceTextEl, shellLabel);
  }

  updateScentHeading() {
    this.setHeadingChoice(this.scentChoiceEl, this.scentChoiceTextEl, this.state.refills[0]?.title);
  }

  onScentToggle(input) {
    // Scents are a radio group — a change event always means a new scent was picked,
    // replacing whichever one was selected before.
    const item = input.closest(".bundle-scent");
    this.state.refills = [{
      productId: item.dataset.scentProductId,
      variantId: item.dataset.scentVariantId,
      price: parseInt(item.dataset.scentPrice, 10),
      firstRefillFreeSellingPlanId: item.dataset.scentFirstRefillFreePlanId || null,
      handle: item.dataset.scentHandle,
      image: item.dataset.scentImage,
      title: item.querySelector(".bundle-scent__name")?.textContent.trim() || item.dataset.scentHandle,
      quantity: 1,
    }];
    if (this.heroImage && item.dataset.scentImage) {
      this.heroImage.src = item.dataset.scentImage;
    }
    this.renderIngredients();
    this.updateScentHeading();
    this.renderScentHeaderPrice();
    this.renderCta();

    // Advance to the Plan step as soon as a scent is chosen.
    if (this.scentStep) this.scentStep.open = false;
    if (this.planStep) this.planStep.open = true;
  }

  /* ----- Renderers ----- */

  updateScentTitle() {
    if (!this.scentTitle) return;
    this.scentTitle.textContent = this.labels.pick_1;
  }

  /* "CHOOSE YOUR SHELL"/"CHOOSE YOUR SCENT" headings always show " - <price>". All shells share
     one price and all scents share one price, so before anything is picked we read it from the
     first option. The scent side shows "FREE" whenever the First Refill Free plan is active. */
  renderShellHeaderPrice() {
    if (!this.shellHeaderPriceEl) return;
    const price = this.state.shell?.price ?? parseInt(this.querySelector(".bundle-shell")?.dataset.shellPrice, 10);
    const text = this.formatMoney(price);
    this.shellHeaderPriceEl.textContent = text ? ` - ${text}` : "";
  }

  renderScentHeaderPrice() {
    if (!this.scentHeaderPriceEl) return;
    const refill = this.state.refills[0];
    if (this.state.variant?.isFirstRefillFree) {
      this.scentHeaderPriceEl.textContent = " - FREE";
      return;
    }
    const price = refill?.price ?? parseInt(this.querySelector(".bundle-scent")?.dataset.scentPrice, 10);
    const text = this.formatMoney(price);
    this.scentHeaderPriceEl.textContent = text ? ` - ${text}` : "";
  }

  renderIngredients() {

    ["ingredients", "howtouse", "howrefills"].forEach((key) => {
      const body = this.accordionBodies[key];
      if (!body) return;

      if (this.state.refills.length === 0) {
        body.innerHTML = this.accordionFallbacks[key] || "";
        return;
      }

      if (key !== "ingredients") {
        const first = this.state.refills[0];
        const data = first && this.accordionData && this.accordionData[first.handle];
        const content = data && data[key];
        body.innerHTML = content || this.accordionFallbacks[key] || "";
        return;
      }

      const seen = new Set();
      const blocks = [];
      this.state.refills.forEach((r) => {
        if (seen.has(r.handle)) return;
        seen.add(r.handle);
        const data = this.accordionData && this.accordionData[r.handle];
        const content = data && data[key];
        if (!content) return;
        if (this.state.refills.length > 1) {
          const niceName = r.handle
            .replace(/^free-/, "")
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          blocks.push(`<h4 class="bundle-ingredients__name">${niceName}</h4><div class="bundle-ingredients__body">${content}</div>`);
        } else {
          blocks.push(content);
        }
      });

      body.innerHTML = blocks.length ? blocks.join("") : (this.accordionFallbacks[key] || "");
    });
  }

  renderCta() {
    if (!this.ctaBtn || !this.state.variant) return;
    this.ctaBtn.disabled = this.state.refills.length === 0 || !this.state.shell;
    // Query fresh each time: submitBundle() swaps the button's innerHTML (Adding… / restore),
    // which detaches any price span cached earlier and would leave the price stuck.
    const priceEl = this.ctaBtn.querySelector("[data-cta-price]");
    if (priceEl) {
      priceEl.textContent = this.formatMoney(this.state.variant.displayPrice);
    }
  }

  formatMoney(pence) {
    if (typeof pence !== "number" || isNaN(pence)) return "";
    const pounds = pence / 100;
    const str = Number.isInteger(pounds) ? pounds.toFixed(0) : pounds.toFixed(2);
    return "£" + str;
  }

  /* ----- Cart submission ----- */

  /* Returns { _plan_name } when the selected plan is in the allowlist (Starter / Discovery),
     otherwise an empty object so it spreads to nothing for one-time purchases. */
  planNameProperty() {
    const name = this.state.variant?.sellingPlanName?.trim();
    if (name && PLAN_NAME_PROPERTY_ALLOWLIST.includes(name.toLowerCase())) {
      return { _plan_name: name };
    }
    return {};
  }

  /* True only for the Discovery Plan: a subscription with 2 refills. The Starter subscription
     (1 refill), one-time purchase, and First Refill Free plan all keep the individual-line-item
     flow — First Refill Free attaches its selling plan directly (see buildIndividualItems),
     found by name on the real scent product, so Recharge Workflows can act on a genuine
     subscription line rather than a bundle (which Recharge doesn't support automations on). */
  isDiscoveryBundle() {
    return (
      !!this.state.variant?.sellingPlanName &&
      this.state.variant?.refillCount === 2
    );
  }

  /* Human-readable list of the chosen refills for display in the cart drawer.
     Items separated by "~~", e.g. "2x Sage & Sea Salt~~1x Bergamot & Cedarwood". */
  buildBundleContents() {
    return this.state.refills
      .map((r) => `${r.quantity}x ${r.title}`)
      .join("~~");
  }

  /* Recharge fixed-price bundle selections — refills only (the shell is not a bundle item). */
  buildBundleSelections() {
    return this.state.refills.map((r) => ({
      collectionId: this.refillsCollectionId,
      externalProductId: r.productId,
      externalVariantId: r.variantId,
      quantity: r.quantity,
    }));
  }

  /* Ask Recharge for the bundle id (_rb_id) that encodes the selected refills.
     Parent = this bundle product + the selected plan variant. */
  async getBundleId() {
    const bundle = {
      externalProductId: this.bundleProductId || this.productId,
      externalVariantId: this.bundleVariantId || this.state.variant.id,
      selections: this.buildBundleSelections(),
    };
    try {
      return await recharge.bundle.getBundleId(bundle);
    } catch (error) {
      console.error("bundle-builder: failed to get Recharge bundle id", error);
      return null;
    }
  }

  /* Each refill + the shell as their own line items, linked by _unique_id. */
  buildIndividualItems(uniqueId) {
    const items = [];

    this.state.refills.forEach((refill, idx) => {
      const props = { _unique_id: uniqueId, ...this.planNameProperty() };
      props[`_prodcut-handle${idx}`] = refill.handle;
      // Attach the scent's own "First Refill Free" selling plan (found directly on the scent
      // product) only when that plan is selected — identified by the plan variant's title, not
      // any selling-plan data of its own. One-time purchase gets no selling plan at all.
      const sellingPlanId = this.state.variant?.isFirstRefillFree ? refill.firstRefillFreeSellingPlanId : null;
      const item = {
        id: refill.variantId,
        quantity: refill.quantity,
        properties: props,
      };
      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }
      items.push(item);
    });

    items.push(this.buildShellItem(uniqueId));
    return items;
  }

  buildShellItem(uniqueId) {
    return {
      id: this.state.shell.variantId,
      quantity: 1,
      properties: {
        _unique_id: uniqueId,
        "_prodcut-handle_shell": this.state.shell.handle,
      },
    };
  }

  async buildCartPayload() {
    const uniqueId = Date.now();

    if (this.isDiscoveryBundle()) {
      const rbId = await this.getBundleId();
      if (!rbId) {
        // Surface the failure rather than silently adding a malformed bundle.
        throw new Error("Could not build the subscription bundle. Please try again.");
      }
      const bundleItem = {
        id: this.bundleVariantId || this.state.variant.id,
        quantity: 1,
        properties: {
          _rb_id: rbId,
          _unique_id: uniqueId,
          _bundle_contents: this.buildBundleContents(),
          ...this.planNameProperty(),
        },
      };
      const sellingPlanId = this.bundleSellingPlanId || this.state.variant.sellingPlanId;
      if (sellingPlanId) {
        bundleItem.selling_plan = sellingPlanId;
      }
      return { items: [bundleItem, this.buildShellItem(uniqueId)] };
    }

    return { items: this.buildIndividualItems(uniqueId) };
  }

  async submitBundle() {
    if (!this.state.variant || !this.state.shell || this.state.refills.length === 0) return;
    const originalHTML = this.ctaBtn.innerHTML;
    this.ctaBtn.disabled = true;
    this.ctaBtn.textContent = "Adding…";

    try {
      const payload = await this.buildCartPayload();
      // TEMP DIAGNOSTIC — remove after debugging. Shows exactly what we send to /cart/add,
      // incl. whether _plan_name made it onto the (discovery) bundle line item.
      console.log("[bundle-builder] sellingPlanName:", this.state.variant?.sellingPlanName, "| isDiscoveryBundle:", this.isDiscoveryBundle(), "| payload items:", JSON.stringify(payload.items, null, 2));
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        payload.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        payload.sections_url = window.location.pathname;
      }

      const res = await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || `Add to cart failed: ${res.status}`);

      // Refresh the cart drawer's rendered sections with the updated cart.
      if (cartDrawer && data.sections) {
        if (typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(data);
        } else {
          // Fallback: replace section innerHTML directly.
          Object.keys(data.sections).forEach((sectionId) => {
            const target = document.getElementById(`shopify-section-${sectionId}`) || document.querySelector(`[data-section-id="${sectionId}"]`);
            if (target) target.innerHTML = data.sections[sectionId];
          });
        }
        // Open the drawer (covers the common Dawn-like and custom APIs).
        if (typeof cartDrawer.open === "function") cartDrawer.open();
        else cartDrawer.classList.add("active", "is-open");
      }

      // Notify anything else listening (analytics, cart count bubble, etc.).
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: data }));

      // Restore the CTA.
      this.ctaBtn.innerHTML = originalHTML;
      this.ctaBtn.disabled = false;
      this.renderCta();
    } catch (err) {
      console.error("bundle-builder:", err);
      // Briefly surface the failure on the CTA, then restore it so the user can retry.
      this.ctaBtn.textContent = err.message || "Something went wrong. Please try again.";
      setTimeout(() => {
        this.ctaBtn.innerHTML = originalHTML;
        this.ctaBtn.disabled = false;
        this.renderCta();
      }, 2500);
    }
  }
}

if (!customElements.get("bundle-builder")) {
  customElements.define("bundle-builder", BundleBuilder);
}
